import type { CircadianAttack } from '../../../data/full/circadien';
import { angleOf, rotate, unitFromAngle } from '../../../shared/angle';
import type { Tempo } from '../../../shared/tempo';
import type { Vec2 } from '../../../shared/vec';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy } from '../../effects';
import type { PlayerState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { type ClockReading, readClock } from '../clock';

const DEGREES = Math.PI / 180;

export interface Sky extends ClockReading {
  sun: boolean;
  moon: boolean;
}

export const circadian: AttackModule<CircadianAttack> = {
  reads: [
    'attackCooldownMul',
    'attackRadiusMul',
    'damageMul',
    'projectileCountAdd',
    'twilightBeatsAdd',
  ],

  step(ctx, player, attack, input) {
    const { state, tempo } = ctx;
    const sky = skyOf(state.tick, tempo, player, attack);
    if (sky.moon) {
      turnCrescents(ctx, player, attack);
    }
    if (!pullTrigger(player, sky.sun && input.fire, attack.cooldownTicks)) {
      return;
    }
    shine(ctx, player, attack);
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  reach(attack) {
    return attack.ray.length;
  },
};

export function skyOf(
  time: number,
  tempo: Pick<Tempo, 'ticksPerBar' | 'ticksPerBeat' | 'originTick'>,
  player: Pick<PlayerState, 'clock' | 'modifiers' | 'suppressedTicks' | 'boosts'>,
  attack: Pick<CircadianAttack, 'halfBars'>,
): Sky {
  const clock = readClock(time, tempo, player.clock, attack.halfBars);
  const twilight =
    clock.flipped && clock.into < statValue(player, 'twilightBeatsAdd', 0) * tempo.ticksPerBeat;
  return { ...clock, sun: !clock.night || twilight, moon: clock.night || twilight };
}

export function starCount(
  player: Pick<PlayerState, 'modifiers' | 'suppressedTicks' | 'boosts'>,
  base: number,
): number {
  return statValue(player, 'projectileCountAdd', base);
}

export function rayWay(aim: Vec2, rank: number, count: number, spreadDegrees: number): Vec2 {
  return rotate(aim, (rank - (count - 1) / 2) * spreadDegrees * DEGREES);
}

// Each bad vibe his rays go through takes one hit, whatever the number of rays.
function shine(ctx: StepContext, player: PlayerState, attack: CircadianAttack): void {
  const { state } = ctx;
  const { ray } = attack;
  const count = starCount(player, 1);
  const half = statValue(player, 'attackRadiusMul', ray.width / 2);
  const damage = statValue(player, 'damageMul', ray.damage);
  const from = { x: player.x, y: player.y };
  const hit = new Set<number>();
  for (let rank = 0; rank < count; rank++) {
    const way = rayWay(player.aim, rank, count, ray.spreadDegrees);
    for (const enemy of state.enemies) {
      if (enemy.hp <= 0 || hit.has(enemy.id)) {
        continue;
      }
      const dx = enemy.x - player.x;
      const dy = enemy.y - player.y;
      const along = dx * way.x + dy * way.y;
      const across = Math.abs(dy * way.x - dx * way.y);
      if (
        along >= -enemy.radius &&
        along <= ray.length + enemy.radius &&
        across <= half + enemy.radius
      ) {
        hit.add(enemy.id);
        hurtEnemy(state, enemy, damage, player.id, from);
      }
    }
  }
}

export function crescentWay(
  time: number,
  tempo: Pick<Tempo, 'ticksPerBeat' | 'originTick'>,
  crescents: Pick<CircadianAttack['crescents'], 'beatsPerTurn'>,
  rank: number,
  count: number,
): Vec2 {
  const turn = crescents.beatsPerTurn * tempo.ticksPerBeat;
  const into = (time - tempo.originTick) % turn;
  const share = (into < 0 ? into + turn : into) / turn;
  return unitFromAngle(2 * Math.PI * (share + rank / count));
}

// A crescent hurts each bad vibe whose middle its way swept over in the tick, near its orbit.
function turnCrescents(ctx: StepContext, player: PlayerState, attack: CircadianAttack): void {
  const { state, tempo } = ctx;
  const { crescents } = attack;
  const count = starCount(player, crescents.count);
  const size = statValue(player, 'attackRadiusMul', crescents.radius);
  const damage = statValue(player, 'damageMul', crescents.damage);
  for (let rank = 0; rank < count; rank++) {
    const was = crescentWay(state.tick - 1, tempo, crescents, rank, count);
    const now = crescentWay(state.tick, tempo, crescents, rank, count);
    const from = { x: player.x + now.x * crescents.orbit, y: player.y + now.y * crescents.orbit };
    for (const enemy of state.enemies) {
      if (enemy.hp <= 0) {
        continue;
      }
      const dx = enemy.x - player.x;
      const dy = enemy.y - player.y;
      const away = Math.sqrt(dx * dx + dy * dy);
      if (
        Math.abs(away - crescents.orbit) <= size + enemy.radius &&
        was.x * dy - was.y * dx >= 0 &&
        dx * now.y - dy * now.x > 0 &&
        dx * (was.x + now.x) + dy * (was.y + now.y) > 0
      ) {
        hurtEnemy(state, enemy, damage, player.id, from);
      }
    }
  }
}
